import { exportVendorListExcel as exportVendorListExcelApi } from "../api/api-methods";
import {
  getVendorDisplayType,
  hasVendorRiskSummary,
  LAST_QUALIFIED_DATE_COLUMN_ENABLED,
  RISK_SUMMARY_COLUMN_ENABLED,
} from "./myVendorsConstants";
import { formatScoreAsPercentage } from "./myVendorRiskUtils";
import {
  formatRequestRowDate,
  getMyVendorRequestDisplayTitle,
  shortenRequestLocation,
} from "./myVendorsRequestUtils";
import { myVendorsCellStyles } from "./exportBadgeStyles";

/** My Vendors tab / banner export — Jackson-master `MyVendors.jsx` getData. */
export const MY_VENDORS_PAGE_EXPORT_HEADERS = [
  "Vendor Name",
  "Type",
  "Qualified Locations",
  "Qualified GxPs",
  "Qualified Services & Capabilities",
  ...(RISK_SUMMARY_COLUMN_ENABLED ? ["Risk Summary"] : []),
];

/** Vendor Lists tab summary — Jackson-master `MyVendors.jsx` lists getData. */
export const VENDOR_LISTS_TAB_EXPORT_HEADERS = ["List Name", "Vendors", "Created"];

/** Per-list CSV — Jackson-master `useVendorLists.exportAsCSV`. */
export const VENDOR_LIST_CSV_HEADERS = [
  "Vendor Name",
  "Type",
  "Primary Location",
  "Services",
];

/**
 * Per-list Excel — Jackson-master VendorListViewGrid column order
 * (the Excel mapper in useVendorLists is stale vs the current grid).
 */
export const VENDOR_LIST_EXCEL_HEADERS = [
  "Vendor Name",
  ...(RISK_SUMMARY_COLUMN_ENABLED ? ["Risk Summary"] : []),
  "Type",
  "Primary Location",
  "GxP Profile",
  "Services",
  "Library Documents",
  "My Requests",
  "Library Requests",
  ...(LAST_QUALIFIED_DATE_COLUMN_ENABLED ? ["Last Qualified Date"] : []),
  "Custom Tags",
  "Added By",
  "Date Added",
];

export const MY_VENDOR_EXPORT_HEADERS = MY_VENDORS_PAGE_EXPORT_HEADERS;
export const VENDOR_LIST_EXPORT_HEADERS = VENDOR_LIST_EXCEL_HEADERS;

function formatExportDate(raw) {
  if (!raw) return "";
  try {
    const match = String(raw).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    }
    const date = new Date(raw);
    if (Number.isNaN(date.getTime())) return String(raw);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return String(raw);
  }
}

function formatVendorType(type) {
  if (!type) return "";
  return getVendorDisplayType(type);
}

function collectServiceNames(vendor = {}) {
  const names = new Set();
  const push = (value) => {
    const trimmed = String(value || "").trim();
    if (trimmed) names.add(trimmed);
  };

  (vendor.serviceCategories || vendor.services || []).forEach((entry) => {
    if (typeof entry === "string") push(entry);
    else push(entry?.rfi_name || entry?.name);
  });
  (vendor.provider?.provider_capabilities || []).forEach((cap) => {
    push(cap?.rfi_name || cap?.name);
  });

  return Array.from(names);
}

function formatRiskSummary(vendor = {}) {
  if (!hasVendorRiskSummary(vendor)) return "";
  const { rating, score } = vendor.riskSummary;
  return `${rating || "Green"} (${formatScoreAsPercentage(score)}%)`;
}

function formatGxpProfile(vendor = {}) {
  const demonstrated = (vendor.demonstratedGxPs || vendor.gxps || []).filter(Boolean);
  const demonstratedSet = new Set(demonstrated);
  const applicableOnly = (vendor.applicableGxPs || []).filter(
    (gxp) => gxp && !demonstratedSet.has(gxp)
  );
  return [...applicableOnly, ...demonstrated].join(", ");
}

function formatLibraryDocuments(vendor = {}) {
  const rfiCount = Number(vendor.rfiCount) || 0;
  const assessmentCount = Number(vendor.assessmentCount) || 0;
  if (rfiCount === 0 && assessmentCount === 0) return "";
  const vqqLabel = `${rfiCount} VQQ${rfiCount === 1 ? "" : "s"}`;
  const auditLabel = `${assessmentCount} Audit${assessmentCount === 1 ? "" : "s"}`;
  return `${vqqLabel}; ${auditLabel}`;
}

function formatRequestEntry(request = {}) {
  const title = getMyVendorRequestDisplayTitle(request) || request.title || "";
  const dateStr = formatRequestRowDate(request.date);
  const location = shortenRequestLocation(request.location);
  const meta = [dateStr, location].filter(Boolean).join(" · ");
  const type = request.type || "";
  const head = [type, title].filter(Boolean).join(": ");
  if (!head && !meta) return "";
  return meta ? `${head} (${meta})` : head;
}

function formatRequests(requests = []) {
  return (requests || []).map(formatRequestEntry).filter(Boolean).join(" | ");
}

function formatTags(vendor = {}) {
  const tags = vendor.tags;
  if (Array.isArray(tags)) {
    return tags
      .map((tag) => (typeof tag === "string" ? tag : tag?.name || tag?.label || ""))
      .filter(Boolean)
      .join(", ");
  }
  return String(tags || "").trim();
}

function formatDateAdded(vendor = {}) {
  if (vendor.dateAdded && !/^\d{4}-\d{2}-\d{2}/.test(String(vendor.dateAdded))) {
    return String(vendor.dateAdded);
  }
  return formatExportDate(vendor.dateAddedRaw || vendor.dateAdded);
}

function listVendorCount(list = {}) {
  if (typeof list.vendorCount === "number") return list.vendorCount;
  const rows = list.members || list.vendors || list.vendorMetadata || [];
  return Array.isArray(rows) ? rows.length : 0;
}

export function mapMyVendorsPageRecord(vendor = {}) {
  const record = {
    "Vendor Name": vendor.name || "",
    Type: formatVendorType(vendor.type),
    "Qualified Locations": vendor.primaryLocation || "",
    "Qualified GxPs": formatGxpProfile(vendor),
    "Qualified Services & Capabilities": collectServiceNames(vendor).join(", "),
  };
  if (RISK_SUMMARY_COLUMN_ENABLED) {
    record["Risk Summary"] = formatRiskSummary(vendor);
  }
  return record;
}

export function mapVendorListSummaryRecord(list = {}) {
  return {
    "List Name": list.name || "",
    Vendors: String(listVendorCount(list)),
    Created: formatExportDate(list.createdAt || list.created),
  };
}

export function mapVendorListCsvRecord(vendor = {}) {
  return {
    "Vendor Name": vendor.name || "",
    Type: formatVendorType(vendor.type),
    "Primary Location": vendor.primaryLocation || "",
    Services: collectServiceNames(vendor).join("; "),
  };
}

export function mapVendorToExportRecord(vendor = {}, { includeListFields = true } = {}) {
  const record = {
    "Vendor Name": vendor.name || "",
    Type: formatVendorType(vendor.type),
    "Primary Location": vendor.primaryLocation || "",
    "GxP Profile": formatGxpProfile(vendor),
    Services: collectServiceNames(vendor).join(", "),
    "Library Documents": formatLibraryDocuments(vendor),
    "My Requests": formatRequests(vendor.myRequests),
    "Library Requests": formatRequests(vendor.libraryRequests),
    "Custom Tags": formatTags(vendor),
  };
  if (RISK_SUMMARY_COLUMN_ENABLED) {
    record["Risk Summary"] = formatRiskSummary(vendor);
  }
  if (LAST_QUALIFIED_DATE_COLUMN_ENABLED) {
    record["Last Qualified Date"] =
      vendor.lastQualifiedDate || formatExportDate(vendor.lastQualifiedRequest?.date);
  }

  if (includeListFields) {
    record["Added By"] = vendor.addedBy || vendor.addedByName || "";
    record["Date Added"] = formatDateAdded(vendor);
  }

  return record;
}

export function buildMyVendorsPageExportData(vendors = []) {
  return {
    headers: [...MY_VENDORS_PAGE_EXPORT_HEADERS],
    records: vendors.map(mapMyVendorsPageRecord),
  };
}

/** @deprecated alias — page-level My Vendors export */
export function buildMyVendorsExportData(vendors = []) {
  return buildMyVendorsPageExportData(vendors);
}

export function buildVendorListsTabExportData(lists = []) {
  return {
    headers: [...VENDOR_LISTS_TAB_EXPORT_HEADERS],
    records: lists.map(mapVendorListSummaryRecord),
  };
}

export function buildVendorListCsvData(members = []) {
  return {
    headers: [...VENDOR_LIST_CSV_HEADERS],
    records: members.map(mapVendorListCsvRecord),
  };
}

export function buildVendorListExcelData(members = []) {
  return {
    headers: [...VENDOR_LIST_EXCEL_HEADERS],
    records: members.map((vendor) => mapVendorToExportRecord(vendor, { includeListFields: true })),
  };
}

function downloadCsv(records, headers, fileName) {
  if (!records.length) return;

  const rows = records.map((record) =>
    headers.map((header) => `"${String(record[header] ?? "").replace(/"/g, '""')}"`).join(",")
  );
  const csvContent = [headers.map((h) => `"${h}"`).join(","), ...rows].join("\n");
  const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.visibility = "hidden";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/** POST vendor list export via API Gateway → SponsorService (same path in all envs). */
export async function postVendorListExport(payload) {
  return exportVendorListExcelApi(payload);
}

export function exportVendorListAsCsv(list, members = []) {
  if (!members.length) return;

  const { records, headers } = buildVendorListCsvData(members);
  const listName = list?.name || "Vendor List";
  downloadCsv(records, headers, `${listName}.csv`);
}

export async function exportVendorListAsExcel(list, members = []) {
  if (!members.length) return;

  const { records, headers } = buildVendorListExcelData(members);
  const data = await postVendorListExport({
    records,
    headers,
    reportTitle: list?.name || "Vendor List",
    reportSubtitle: list?.notes || "",
    cellStyles: myVendorsCellStyles,
  });

  if (!data?.success) {
    throw new Error(data?.error || "Excel export failed");
  }

  if (data.excel_url) {
    window.open(data.excel_url, "_blank");
    return;
  }

  throw new Error("Excel export did not return a download URL");
}
